// src/components/NewSurveyForm.jsx
import axios from "axios"
import IconButton from "components/Common/IconButton/IconButton"
import config from "config/config"
import Select from "react-select"
import React, { useEffect, useState } from "react"
import CreatableSelect from "react-select/creatable"
import { useSelector } from "react-redux"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Input,
  Label,
  Row,
  Button,
} from "reactstrap"
import { DeleteOutline } from "@mui/icons-material"

const FIELD_HEIGHT = 45

// Enforce react-select to visually match reactstrap <Input/>
const getSelectStyles = mode => ({
  control: (base, state) => ({
    ...base,
    minHeight: FIELD_HEIGHT,
    height: FIELD_HEIGHT,
    borderRadius: 8,
    border: "1px solid #e0e0e0",
    boxShadow: "none",
    paddingLeft: 30, // keep left padding parity with Inputs
    backgroundColor: "transparent",
    ":hover": { borderColor: state.isFocused ? "#2684FF" : "#e0e0e0" },
  }),
  valueContainer: base => ({
    ...base,
    padding: "0 8px",
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  singleValue: base => ({
    ...base,
    color: mode === "dark" ? "#fff" : "#000",
  }),
  indicatorsContainer: base => ({
    ...base,
    height: FIELD_HEIGHT,
  }),
  dropdownIndicator: base => ({
    ...base,
    padding: 8,
  }),
  clearIndicator: base => ({
    ...base,
    padding: 8,
  }),
  multiValue: base => ({
    ...base,
    margin: 2,
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({ ...base, zIndex: 9999 }),
})

// Square flex button so the icon is perfectly centered.
const deleteBtnStyle = {
  width: FIELD_HEIGHT,
  height: FIELD_HEIGHT,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mt: 5,
}

const NewSurveyForm = ({
  formData,
  setFormData,
  setErr,
  setSuccess,
  colors,
  doctors,
  mode,
}) => {
  const [audiences, setAudiences] = useState(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)

  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const fetchAudience = async () => {
    setLoading(true)
    try {
      const response = await axios.get(
        `${config.backendUrl}/audience/getOrganizationAudience/${activeOrganization.id}`,
        { headers: { Authorization: `Bearer ${userData.token}` } },
      )
      setAudiences(response.data.audiences)
      setLoading(false)
    } catch (err) {
      console.log(err)
      setErr("Error fetching audiences.")
    }
  }

  const handleInputChange = (e, surveyIndex) => {
    const { name, value } = e.target
    setFormData(prevState => {
      const updatedSurveys = [...prevState.survey]
      updatedSurveys[surveyIndex] = {
        ...updatedSurveys[surveyIndex],
        [name]: value,
      }
      return { ...prevState, survey: updatedSurveys }
    })
  }

  const handleQuestionChange = (e, surveyIndex, questionIndex) => {
    const { name, value } = e.target
    setFormData(prevState => {
      const updatedSurveys = [...prevState.survey]
      updatedSurveys[surveyIndex].questions[questionIndex] = {
        ...updatedSurveys[surveyIndex].questions[questionIndex],
        [name]: value,
      }
      return { ...prevState, survey: updatedSurveys }
    })
  }

  const deleteQuestion = (surveyIndex, questionIndex) => {
    setFormData(prevState => {
      const updatedSurveys = [...prevState.survey]
      updatedSurveys[surveyIndex].questions = updatedSurveys[
        surveyIndex
      ].questions.filter((_, idx) => idx !== questionIndex)
      return { ...prevState, survey: updatedSurveys }
    })
  }

  const getCategorySuggestions = () => {
    const categorySet = new Set(categories)
    formData.survey.forEach(s =>
      s.questions.forEach(q => q.category && categorySet.add(q.category)),
    )
    return Array.from(categorySet).map(c => ({ value: c, label: c }))
  }

  const handleAudienceChange = (selectedOptions, surveyIndex) => {
    setFormData(prevState => {
      const updatedSurveys = [...prevState.survey]
      updatedSurveys[surveyIndex] = {
        ...updatedSurveys[surveyIndex],
        audiences: selectedOptions,
      }
      return { ...prevState, survey: updatedSurveys }
    })
  }

  const handleDoctorChange = selectedOption => {
    setFormData(prev => ({
      ...prev,
      doctor: selectedOption ? selectedOption.value : null, // store doctor ID
    }))
  }

  const addQuestion = surveyIndex => {
    if (formData.survey[surveyIndex].questions.length >= 20) {
      setErr("Cannot add more than 20 queestions in a survey")
      return
    }
    setFormData(prevState => {
      const updatedSurveys = [...prevState.survey]
      updatedSurveys[surveyIndex].questions.push({
        questionText: "",
        questionType: "text",
        options: ["", "", "", ""],
        category: "",
      })
      return { ...prevState, survey: updatedSurveys }
    })
  }

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/survey/getCategoriesByOrg/${activeOrganization.id}`,
        { headers: { Authorization: `Bearer ${userData.token}` } },
      )
      const categoryNames = response.data.categories.map(cat => cat.name)
      setCategories(categoryNames)
    } catch (err) {
      console.log(err)
      setErr("Error fetching categories.")
    }
  }

  const handleCategoryChange = (selectedOption, surveyIndex, questionIndex) => {
    setFormData(prevState => {
      const updatedSurveys = [...prevState.survey]
      updatedSurveys[surveyIndex].questions[questionIndex] = {
        ...updatedSurveys[surveyIndex].questions[questionIndex],
        category: selectedOption ? selectedOption.value : "",
      }
      return { ...prevState, survey: updatedSurveys }
    })
  }

  const deleteSurvey = surveyIndex => {
    setFormData(prevState => ({
      ...prevState,
      survey: prevState.survey.filter((_, index) => index !== surveyIndex),
    }))
  }

  useEffect(() => {
    fetchAudience()
    fetchCategories()
  }, [])

  useEffect(() => {
    console.log(formData, "ff")
  }, [formData])

  return (
    <div>
      {formData?.survey?.map((survey, index) => (
        <Row className="justify-content-center" key={index}>
          <Col xl={12}>
            <Card className="shadow-sm" style={{ borderRadius: "22px" }}>
              <CardBody>
                <Row className="align-items-center">
                  <Col
                    xl={6}
                    lg={6}
                    md={6}
                    className="d-flex align-items-center"
                  >
                    <CardTitle
                      className="h2 mb-0 text-uppercase"
                      style={{
                        margin: 0,
                        fontSize: 16,
                        color: colors.text,
                        fontWeight: 700,
                      }}
                      onClick={() => console.log("Doctors", doctors)}
                    >
                      Survey Form
                    </CardTitle>
                  </Col>
                  <Col
                    xl={6}
                    lg={6}
                    md={6}
                    className="d-flex justify-content-end align-items-center"
                  >
                    <IconButton
                      color="danger"
                      size="md"
                      onClick={() => deleteSurvey(index)}
                      text="Remove"
                      icon="mdi-cancel"
                    />
                  </Col>
                </Row>

                <Row className="align-items-center">
                  <Col md={6}>
                    <div className="mt-3 mb-3">
                      <Label
                        className="form-label"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          color: mode === "dark" ? "#fff" : "#666",
                        }}
                      >
                        Survey Title
                      </Label>
                      <div
                        style={{ position: "relative", height: FIELD_HEIGHT }}
                      >
                        <Input
                          name="title"
                          className="form-control"
                          placeholder="Enter Survey Title"
                          type="text"
                          disabled={survey.surveyId}
                          value={survey.title}
                          onChange={e => handleInputChange(e, index)}
                          style={{
                            paddingLeft: "44px",
                            height: "100%",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            color: mode === "dark" ? "#fff" : "#000",
                          }}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="">
                      <Label
                        className="form-label"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          color: mode === "dark" ? "#fff" : "#666",
                        }}
                      >
                        Survey Description
                      </Label>
                      <div
                        style={{ position: "relative", height: FIELD_HEIGHT }}
                      >
                        <Input
                          name="description"
                          className="form-control"
                          placeholder="Enter Survey Description"
                          type="text"
                          disabled={survey.surveyId}
                          value={survey.description}
                          onChange={e => handleInputChange(e, index)}
                          style={{
                            paddingLeft: "44px",
                            height: "100%",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            color: mode === "dark" ? "#fff" : "#000",
                          }}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="">
                      <Label
                        className="form-label"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          color: mode === "dark" ? "#fff" : "#666",
                        }}
                      >
                        Survey Threshold
                      </Label>
                      <div
                        style={{ position: "relative", height: FIELD_HEIGHT }}
                      >
                        <Input
                          name="threshold"
                          className="form-control"
                          placeholder="Enter Survey Threshold (1-10)"
                          type="text"
                          disabled={survey.surveyId}
                          value={survey.threshold}
                          onChange={e => handleInputChange(e, index)}
                          style={{
                            paddingLeft: "44px",
                            height: "100%",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            color: mode === "dark" ? "#fff" : "#000",
                          }}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <Label
                        className="form-label"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          color: mode === "dark" ? "#fff" : "#666",
                        }}
                      >
                        Associate Doctor
                      </Label>
                      <div
                        style={{ position: "relative", height: FIELD_HEIGHT }}
                      >
                        {doctors && doctors.length > 0 ? (
                          <Select
                            options={doctors.map(doctor => ({
                              value: doctor.id,
                              label: `${doctor.firstName} ${doctor.lastName}`,
                            }))}
                            name="doctor"
                            className="basic-select"
                            classNamePrefix="select"
                            // value comes from top-level formData.doctor (doctor ID)
                            value={
                              formData.doctor
                                ? (() => {
                                    const selectedDoc = doctors.find(
                                      d => d.id === formData.doctor,
                                    )
                                    return selectedDoc
                                      ? {
                                          value: selectedDoc.id,
                                          label: `${selectedDoc.firstName} ${selectedDoc.lastName}`,
                                        }
                                      : null
                                  })()
                                : null
                            }
                            onChange={handleDoctorChange}
                            styles={getSelectStyles(mode)}
                            menuPortalTarget={document.body}
                          />
                        ) : (
                          <Select
                            options={[
                              {
                                value: "",
                                label: "No Doctors Available",
                                isDisabled: true,
                              },
                            ]}
                            name="doctor"
                            className="basic-select"
                            value={null}
                            classNamePrefix="select"
                            styles={getSelectStyles(mode)}
                            menuPortalTarget={document.body}
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-group">
                      <Label
                        className="form-label"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          color: mode === "dark" ? "#fff" : "#666",
                        }}
                      >
                        Audience Selection
                      </Label>
                      <div
                        style={{ position: "relative", height: FIELD_HEIGHT }}
                      >
                        {audiences && audiences.length > 0 ? (
                          <Select
                            options={audiences.map(org => ({
                              value: org.id,
                              label: org.name,
                            }))}
                            isMulti
                            name="audiences"
                            className="basic-multi-select"
                            value={survey.audiences || []}
                            classNamePrefix="select"
                            onChange={selectedOptions =>
                              handleAudienceChange(selectedOptions, index)
                            }
                            styles={getSelectStyles(mode)}
                            menuPortalTarget={document.body}
                          />
                        ) : (
                          <Select
                            options={[
                              {
                                value: "",
                                label: "No Audiences Available",
                                isDisabled: true,
                              },
                            ]}
                            isMulti
                            name="audiences"
                            className="basic-multi-select"
                            value={[]}
                            classNamePrefix="select"
                            styles={getSelectStyles(mode)}
                            menuPortalTarget={document.body}
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Questions Section */}
                <Row>
                  <CardTitle
                    className="h2 my-4 text-uppercase"
                    style={{
                      margin: 0,
                      fontSize: 16,
                      color: colors.text,
                      fontWeight: 700,
                    }}
                  >
                    Questions
                  </CardTitle>
                </Row>

                {survey?.questions?.map((question, questionIndex) => (
                  <Row className="mb-3" key={questionIndex}>
                    <Col md={5}>
                      <Label
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          color: mode === "dark" ? "#fff" : "#666",
                        }}
                      >
                        Question
                      </Label>
                      <div
                        style={{ position: "relative", height: FIELD_HEIGHT }}
                      >
                        <Input
                          type="text"
                          name="questionText"
                          placeholder="Enter question text"
                          disabled={survey.surveyId}
                          value={question.questionText}
                          onChange={e =>
                            handleQuestionChange(e, index, questionIndex)
                          }
                          style={{
                            paddingLeft: "44px",
                            height: "100%",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            color: mode === "dark" ? "#fff" : "#000",
                          }}
                        />
                      </div>
                    </Col>

                    <Col md={3}>
                      <Label
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          color: mode === "dark" ? "#fff" : "#666",
                        }}
                      >
                        Type
                      </Label>
                      <div
                        style={{ position: "relative", height: FIELD_HEIGHT }}
                      >
                        <Input
                          type="select"
                          name="questionType"
                          value={question.questionType}
                          disabled={survey.surveyId}
                          onChange={e =>
                            handleQuestionChange(e, index, questionIndex)
                          }
                          style={{
                            paddingLeft: "44px",
                            height: "100%",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            color: mode === "dark" ? "#fff" : "#000",
                          }}
                        >
                          <option value="text">Text</option>
                          <option value="rating">Rating</option>
                          <option value="multiple choice">
                            Multiple Choice
                          </option>
                        </Input>
                      </div>
                    </Col>

                    <Col md={3}>
                      <Label
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          color: mode === "dark" ? "#fff" : "#666",
                        }}
                      >
                        Category
                      </Label>
                      <div
                        style={{ position: "relative", height: FIELD_HEIGHT }}
                      >
                        <CreatableSelect
                          style={{
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                          isClearable
                          options={getCategorySuggestions()}
                          value={
                            question.category
                              ? {
                                  value: question.category,
                                  label: question.category,
                                }
                              : null
                          }
                          onChange={selectedOption =>
                            handleCategoryChange(
                              selectedOption,
                              index,
                              questionIndex,
                            )
                          }
                          onCreateOption={newCategory => {
                            // why: keep local suggestions in sync when user adds new
                            setCategories(prev => [...prev, newCategory])
                            handleCategoryChange(
                              { value: newCategory, label: newCategory },
                              index,
                              questionIndex,
                            )
                          }}
                          placeholder="Select or create category"
                          styles={getSelectStyles(mode)}
                          menuPortalTarget={document.body}
                        />
                      </div>
                    </Col>

                    <Col md={1} className="d-flex align-items-end">
                      <Button
                        color="danger"
                        size="sm"
                        style={deleteBtnStyle}
                        disabled={survey.surveyId}
                        onClick={() => deleteQuestion(index, questionIndex)}
                        aria-label="Delete question"
                      >
                        <DeleteOutline />
                      </Button>
                    </Col>

                    {question.questionType === "multiple choice" && (
                      <Col md={12} className="mt-2">
                        <Label>Options</Label>
                        <Row>
                          {(question?.options || []).map(
                            (option, optionIndex) => (
                              <Col md={3} key={optionIndex}>
                                <Input
                                  type="text"
                                  placeholder={`Option ${optionIndex + 1}`}
                                  value={option}
                                  disabled={survey.surveyId}
                                  onChange={e => {
                                    const newOptions = [
                                      ...(question.options || []),
                                    ]
                                    newOptions[optionIndex] = e.target.value
                                    handleQuestionChange(
                                      {
                                        target: {
                                          name: "options",
                                          value: newOptions,
                                        },
                                      },
                                      index,
                                      questionIndex,
                                    )
                                  }}
                                  style={{
                                    height: FIELD_HEIGHT,
                                    borderRadius: 8,
                                    border: "1px solid #e0e0e0",
                                  }}
                                />
                              </Col>
                            ),
                          )}
                        </Row>
                      </Col>
                    )}
                  </Row>
                ))}

                <Button
                  disabled={survey.surveyId}
                  onClick={() => addQuestion(index)}
                  style={{
                    backgroundColor: "#2990DF",
                    color: "white",
                    padding: "10px 30px",
                    fontSize: "15px",
                    fontWeight: "500",
                    borderRadius: "8px",
                  }}
                >
                  + Add Question
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ))}
    </div>
  )
}

export default NewSurveyForm
